import React, { useState, useRef, useEffect } from 'react';
import Icon from 'components/AppIcon';
import { ASSETS } from '../data/assetData';

const SECTORS = [...new Set(ASSETS.map((a) => a.sector))];
const COUNTRIES = [...new Set(ASSETS.map((a) => a.country))];
const FUNDS = [...new Set(ASSETS.map((a) => a.fund))];
const STATUSES = ['Operational', 'In Construction'];
const RISK_CATEGORIES = ['Low (1–2)', 'Medium (3)', 'High (4–5)'];

const REVIEW_STATUS_COLOURS = {
  Draft: 'bg-muted text-muted-foreground',
  Submitted: 'bg-blue-100 text-blue-700',
  Reviewed: 'bg-success/10 text-success',
  Closed: 'bg-muted text-foreground',
};

export default function AssetSelector({ selectedIds, onChange }) {
  const [open, setOpen] = useState(false);
  const [filters, setFilters] = useState({ sector: '', country: '', fund: '', status: '', risk: '' });
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref?.current && !ref?.current?.contains(e?.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = ASSETS?.filter((a) => {
    if (filters?.sector && a?.sector !== filters?.sector) return false;
    if (filters?.country && a?.country !== filters?.country) return false;
    if (filters?.fund && a?.fund !== filters?.fund) return false;
    if (filters?.status && a?.stage !== filters?.status) return false;
    if (filters?.risk) {
      if (filters?.risk === 'Low (1–2)' && a?.riskScore > 2) return false;
      if (filters?.risk === 'Medium (3)' && a?.riskScore !== 3) return false;
      if (filters?.risk === 'High (4–5)' && a?.riskScore < 4) return false;
    }
    return true;
  });

  const toggleAsset = (id) => {
    if (selectedIds?.includes(id)) onChange(selectedIds?.filter((x) => x !== id));
    else onChange([...selectedIds, id]);
  };

  const selectAll = () => onChange(filtered?.map((a) => a?.id));
  const clearAll = () => onChange([]);

  const selectedAssets = ASSETS?.filter((a) => selectedIds?.includes(a?.id));

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((p) => !p)}
        className="flex items-center gap-2 px-3 py-2 bg-card border border-border rounded-md text-sm font-caption text-foreground hover:border-primary/50 transition-all duration-250 min-w-[220px] max-w-[320px]"
      >
        <Icon name="Layers" size={15} color="var(--color-primary)" strokeWidth={2} />
        <span className="flex-1 text-left truncate">
          {selectedIds?.length === 0 ? 'All assets' : selectedIds?.length === ASSETS?.length ? 'All assets' : `${selectedIds?.length} asset${selectedIds?.length > 1 ? 's' : ''} selected`}
        </span>
        <Icon name={open ? 'ChevronUp' : 'ChevronDown'} size={14} color="currentColor" strokeWidth={2} />
      </button>
      {open && (
        <div
          className="absolute top-full left-0 mt-1 w-[480px] bg-popover border border-border rounded-lg shadow-dropdown overflow-hidden"
          style={{ zIndex: 'var(--z-dropdown)' }}
        >
          {/* Filters */}
          <div className="p-3 border-b border-border bg-muted/30 grid grid-cols-2 gap-2">
            {[
              { key: 'sector', label: 'Sector', options: SECTORS },
              { key: 'country', label: 'Country', options: COUNTRIES },
              { key: 'fund', label: 'Fund', options: FUNDS },
              { key: 'status', label: 'Status', options: STATUSES },
              { key: 'risk', label: 'Risk Category', options: RISK_CATEGORIES },
            ]?.map(({ key, label, options }) => (
              <select
                key={key}
                value={filters?.[key]}
                onChange={(e) => setFilters((p) => ({ ...p, [key]: e?.target?.value }))}
                className="text-xs px-2 py-1.5 border border-border rounded bg-card text-foreground font-caption"
              >
                <option value="">{label}: All</option>
                {options?.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            ))}
            <div className="flex items-center gap-2">
              <button onClick={selectAll} className="text-xs text-primary hover:underline font-caption">Select all</button>
              <span className="text-muted-foreground">·</span>
              <button onClick={clearAll} className="text-xs text-muted-foreground hover:text-foreground font-caption">Clear</button>
            </div>
          </div>
          {/* Asset list */}
          <div className="max-h-64 overflow-y-auto">
            {filtered?.map((asset) => (
              <button
                key={asset?.id}
                onClick={() => toggleAsset(asset?.id)}
                className={`flex items-center gap-3 w-full px-4 py-2.5 text-left hover:bg-muted transition-colors ${
                  selectedIds?.includes(asset?.id) ? 'bg-primary/5' : ''
                }`}
              >
                <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                  selectedIds?.includes(asset?.id) ? 'bg-primary border-primary' : 'border-border'
                }`}>
                  {selectedIds?.includes(asset?.id) && <Icon name="Check" size={10} color="white" strokeWidth={3} />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-caption font-medium text-foreground truncate">{asset?.name}</p>
                  <p className="text-xs text-muted-foreground">{asset?.sector} · {asset?.country}</p>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full font-caption font-medium flex-shrink-0 ${REVIEW_STATUS_COLOURS?.[asset?.reviewStatus]}`}>
                  {asset?.reviewStatus}
                </span>
              </button>
            ))}
            {filtered?.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-6 font-caption">No assets match filters</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
